import type {
    IsoAndUnixTimestamp} from "@@/shared/date-time/iso-and-unix";
import {
    isoAndUnixZodSchema,
    nowIsoAndUnix,
} from "@@/shared/date-time/iso-and-unix";
import { idZodSchema } from "@@/shared/id";
import type {
    ProviderId} from "@@/shared/providers/provider-id";
import {
    providerIdZodSchema,
} from "@@/shared/providers/provider-id";
import type {
    RequiredTranslatable,
    Translatable} from "@@/shared/translations/translation";
import {
    requiredTranslatableZodSchema,
    translatableZodSchema,
} from "@@/shared/translations/translation";
import type { AvailableQuantity } from "@@/shared/units-and-quantities/quantity";
import type { ZodObjectOf } from "@@/shared/zod-helpers";
import { generateId } from "@@/utilities/generate-id";
import type { Except } from "type-fest";
import { z } from "zod";
import type { ResourceId} from "../resource-id";
import { resourceIdZodSchema } from "../resource-id";

type ResourceEditCommand = {
    readonly _id: string;
    readonly _type: "RESOURCE_EDIT_COMMAND";
    readonly _createdAt: IsoAndUnixTimestamp;
    readonly providerId: ProviderId;
    readonly resourceId: ResourceId;

    readonly title: RequiredTranslatable;
    readonly subtitle: Translatable;
    readonly description: Translatable;

    readonly quantity: AvailableQuantity;
    readonly allowMultipleCheckIns: boolean;
    readonly checkInEnabled: boolean;
    readonly type: "RESOURCE_TABLE" | "RESOURCE_GENERIC" | "RESOURCE_RESERVED";
};

type FactoryParams = Except<
    ResourceEditCommand,
    "_id" | "_type" | "_createdAt"
>;
const resourceEditCommandFactory = (params: FactoryParams) => {
    const ResourceEditCommand: ResourceEditCommand = {
        ...params,
        _id: generateId({ prefix: "resource_edit_command__" }),
        _type: "RESOURCE_EDIT_COMMAND",
        _createdAt: nowIsoAndUnix(),
    };
    return ResourceEditCommand;
};

const resourceEditCommandZodObject = z.object({
    _id: idZodSchema,
    _type: z.literal("RESOURCE_EDIT_COMMAND"),
    _createdAt: isoAndUnixZodSchema,
    type: z.enum(["RESOURCE_TABLE", "RESOURCE_GENERIC", "RESOURCE_RESERVED"]),
    providerId: providerIdZodSchema,
    resourceId: resourceIdZodSchema,
    title: requiredTranslatableZodSchema,
    subtitle: translatableZodSchema,
    description: translatableZodSchema,
    quantity: z.number().int().min(0),
    allowMultipleCheckIns: z.boolean(),
    checkInEnabled: z.boolean(),
}) satisfies ZodObjectOf<ResourceEditCommand>;

const resourceEditCommandFactoryParamsZodObject =
    resourceEditCommandZodObject.omit({
        _id: true,
        _type: true,
        _createdAt: true,
    });

export {
    resourceEditCommandFactory,
    resourceEditCommandFactoryParamsZodObject,
    resourceEditCommandZodObject,
};
export type {
    ResourceEditCommand,
    FactoryParams as ResourceEditCommandFactoryParams,
};
